import { useMemo } from 'react';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useScreenerRuleOptions } from '@/hooks/useScreenerRuleOptions';
import type { ScreenerRuleRow } from '@/types/appRouter';
import SearchSelect, { type SearchSelectFormProps, type SearchSelectItem } from './SearchSelect';

export interface ScreenerRuleSelectProps extends SearchSelectFormProps {
    value: number | null;
    onChange: (screenerRuleId: number | null) => void;
}

const getRuleBadge = (rule: ScreenerRuleRow): string | undefined => {
    if (rule.supersededById != null) return 'Superseded';
    if (rule.isDraft) return 'Draft';
    if (!rule.isActive) return 'Inactive';

    return undefined;
};

const toItem = (rule: ScreenerRuleRow): SearchSelectItem => {
    const rootId = rule.rootRuleId ?? rule.id;
    const revisionLabel = `#${rootId}.r${rule.revision}`;

    return {
        badge: getRuleBadge(rule),
        label: `${rule.name} (${revisionLabel})`,
        meta: rule.description ? `${revisionLabel} · ${rule.description}` : revisionLabel,
        name: rule.name,
        searchText: `${revisionLabel} ${rule.description ?? ''}`.toLowerCase(),
        value: rule.id
    };
};

const missingItem = (screenerRuleId: number): SearchSelectItem => ({
    label: `Screener Rule ${screenerRuleId} (#${screenerRuleId})`,
    meta: 'Not in your current screener list',
    name: `Screener Rule ${screenerRuleId}`,
    value: screenerRuleId
});

/**
 * Searchable single select for screener rules. Fetches its own data via
 * useScreenerRuleOptions, which also resolves a selected rule that is missing
 * from the list (e.g. superseded revisions). Search matches name, id, the
 * #root.rRevision label, and the rule description.
 */
const ScreenerRuleSelect = (props: ScreenerRuleSelectProps) => {
    const { value, onChange, placeholder, isDisabled = false, ...formProps } = props;

    const { listQuery, metaQuery, rules } = useScreenerRuleOptions(value, {
        enabled: !isDisabled,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const items = useMemo(() => rules.map(toItem), [rules]);

    const queryError = listQuery.error ?? metaQuery.error;
    const loadError = queryError ? `Failed to load screener rules: ${queryError.message}` : undefined;

    return (
        <SearchSelect
            {...formProps}
            isDisabled={isDisabled}
            isLoading={listQuery.isLoading}
            items={items}
            loadError={loadError}
            mode="single"
            placeholder={placeholder ?? 'Select a screener rule...'}
            resolveMissingItem={missingItem}
            value={value}
            onChange={onChange}
        />
    );
};

export default ScreenerRuleSelect;
